import { calculateIkrProgress } from './utils';

/**
 * The individual key results one person owns, in the shape the evaluation UI
 * needs — flattened out of the OKR tree, with the tracking-page numbers already
 * computed and the FY26-27 owner-thread shape normalised away.
 *
 * Lifted verbatim out of EvaluationsView's `personIKRs` memo so the Evaluations
 * Summary response viewer can derive exactly the same list. Both surfaces key
 * evaluation documents by `ikr.id`, so any drift between two copies of this walk
 * would show up as responses silently going missing from one of them.
 *
 * The two financial years have different depths: FY25-26 is
 * objective → keyResults → individualKRs, FY26-27 adds a team-KR layer in
 * between. FY26-27 IKRs are owner threads (`quarters[]`); older documents in the
 * same collection may still carry the flat text / kpiTarget / quarterlyActuals
 * shape, which is why every read here is written to accept both.
 *
 * @param {?string} personId  the owner's uid; falsy returns []
 * @param {Object}  sources   { okrs, okrsFY2627, fy }
 * @returns {Array} IKRs decorated with objective/KR context and quarterly actuals
 */
export const getPersonIkrs = (personId, { okrs, okrsFY2627, fy }) => {
  if (!personId) return [];

  const allIKRs = [];

  if (fy === 'FY2627') {
    // 4-level hierarchy: Objective -> KR -> Team KR -> Individual KR
    if (!Array.isArray(okrsFY2627)) return [];
    okrsFY2627.forEach(obj => {
      obj.keyResults?.forEach(kr => {
        kr.teamKRs?.forEach(tkr => {
          tkr.individualKRs?.forEach(ikr => {
            if (ikr.ownerId === personId) {
              // FY26-27 IKRs are owner threads (quarters[]); older docs may
              // still carry the flat text / kpiTarget / quarterlyActuals shape.
              const isThread = Array.isArray(ikr.quarters);
              const quarterActuals = isThread
                ? [0, 1, 2, 3].map(i => ikr.quarters[i]?.actual ?? null)
                : (ikr.quarterlyActuals || [null, null, null, null]);
              const quarterTargets = isThread
                ? [0, 1, 2, 3].map(i => ikr.quarters[i]?.target ?? null)
                : [null, null, null, null];

              allIKRs.push({
                ...ikr,
                objectiveCode: obj.code,
                objectiveText: obj.objective,
                objectiveName: `${obj.code}: ${obj.objective}`,
                teamKrName: tkr.text,
                teamKrTarget: tkr.targetKPI ?? null,
                teamKrUnit: tkr.unit || '',
                krCode: kr.code,
                krText: kr.text,
                // Mirror the tracking view: fall back to the first quarter
                // that has text when a thread carries no yearly text.
                krName: (isThread ? ikr.yearlyText : ikr.text)
                  || (isThread ? ([0, 1, 2, 3].map(i => ikr.quarters[i]?.text).find(t => t) || '') : ''),
                targetKpi: (isThread ? ikr.yearlyTarget : ikr.kpiTarget) ?? null,
                kpiTarget: (isThread ? ikr.yearlyTarget : ikr.kpiTarget) ?? null,
                calculationMethod: ikr.calculationMethod || 'sum',
                quarterTexts: isThread ? [0, 1, 2, 3].map(i => ikr.quarters[i]?.text || '') : ['', '', '', ''],
                quarterTargets,
                q1Actual: quarterActuals[0],
                q2Actual: quarterActuals[1],
                q3Actual: quarterActuals[2],
                q4Actual: quarterActuals[3],
                quarterlyActuals: quarterActuals,
                // Computed from the raw doc so this matches OKR Tracking exactly.
                trackingProgress: calculateIkrProgress(ikr)
              });
            }
          });
        });
      });
    });
  } else {
    if (!Array.isArray(okrs)) return [];
    okrs.forEach(okr => {
      okr.keyResults?.forEach(tkr => {
        tkr.individualKRs?.forEach(ikr => {
          if (ikr.ownerId === personId) {
            const quarterActuals = ikr.quarterlyActuals || [null, null, null, null];
            allIKRs.push({
              ...ikr,
              objectiveCode: okr.code || '',
              objectiveText: okr.objective || '',
              // FY25-26 objectives store the title on `objective`, not `objectiveName`.
              objectiveName: [okr.code, okr.objective].filter(Boolean).join(': '),
              teamKrName: tkr.text,
              teamKrTarget: tkr.kpiTarget ?? null,
              teamKrUnit: tkr.kpiUnit || '',
              krName: ikr.text || '',
              targetKpi: ikr.kpiTarget ?? null,
              kpiTarget: ikr.kpiTarget ?? null,
              calculationMethod: ikr.calculationMethod || 'sum',
              quarterTexts: ['', '', '', ''],
              quarterTargets: [null, null, null, null],
              q1Actual: quarterActuals[0] ?? null,
              q2Actual: quarterActuals[1] ?? null,
              q3Actual: quarterActuals[2] ?? null,
              q4Actual: quarterActuals[3] ?? null,
              quarterlyActuals: quarterActuals,
              trackingProgress: calculateIkrProgress(ikr)
            });
          }
        });
      });
    });
  }

  return allIKRs;
};

/** Which evaluations subcollection a financial year stores its documents in. */
export const evalSubcollectionFor = (fy) => (fy === 'FY2627' ? 'evaluations_fy2627' : 'evaluations');
